import React, { useState } from 'react';
import { Box, AppBar, Toolbar, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Breadcrumb from './Breadcrumb';
import GlobalAttendanceToast from './GlobalAttendanceToast';
import GlobalPeopleSearch from './GlobalPeopleSearch';
import GoLiveToggle from './GoLiveToggle';
import { DashboardLiveProvider } from '../contexts/DashboardLiveContext';
import { useGlobalNotificationUi } from '../contexts/GlobalNotificationUiContext';

const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 84;
const SIDEBAR_TRANSITION_DURATION_MS = 320;
const SIDEBAR_TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

// Fixed bell: right 18–28px + ~40px icon. Reserve space only when visible.
const BELL_CLEARANCE = { xs: 7, sm: 7, md: 8 };

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { isBellVisible } = useGlobalNotificationUi();
  const isAttendanceDashboard = location.pathname === '/dashboard';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopCollapseToggle = () => {
    setDesktopCollapsed((prev) => !prev);
  };

  const desktopSidebarWidth = desktopCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const header = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: 2 },
        width: '100%',
        minWidth: 0,
        mb: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ minWidth: 0, flexShrink: 0 }}>
        <Breadcrumb sx={{ mb: 0, flexShrink: 0 }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'nowrap',
          gap: { xs: 1, sm: 1.5 },
          minWidth: 0,
          ml: { sm: 'auto' },
          // Sit beside the fixed bell; collapse when notifications are off.
          pr: isBellVisible ? BELL_CLEARANCE : 0,
          transition: 'padding-right 0.2s ease',
        }}
      >
        {isAttendanceDashboard ? (
          <Box sx={{ flexShrink: 0 }}>
            <GoLiveToggle />
          </Box>
        ) : null}
        <Box
          sx={{
            minWidth: 0,
            width: { xs: '100%', sm: 260, md: 300 },
            // maxWidth: { xs: 'min(100%, 260px)', sm: 300 },
            flex: '0 1 auto',
          }}
        >
          <GlobalPeopleSearch
            sx={{
              mb: 0,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  const mainContent = (
    <>
      {isMobile ? <Toolbar /> : null}
      {header}
      {children}
    </>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            display: { md: 'none' },
            backgroundColor: '#fff',
            color: 'text.primary',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Sidebar
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        desktopCollapsed={desktopCollapsed}
        onDesktopCollapseToggle={handleDesktopCollapseToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          backgroundColor: '#fafafa',
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${desktopSidebarWidth}px)` },
          transition: `width ${SIDEBAR_TRANSITION_DURATION_MS}ms ${SIDEBAR_TRANSITION_EASING}`,
          willChange: 'width',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <DashboardLiveProvider>
          {mainContent}
        </DashboardLiveProvider>
      </Box>
      <GlobalAttendanceToast />
    </Box>
  );
};

export default Layout;
